/**
 * Экспорт разбора в PDF.
 *
 * Документ собирается как отдельная печатная страница и отдаётся системному
 * диалогу печати — там пользователь сохраняет его в PDF. Так документ выходит
 * в фирменных цветах и шрифтах без дополнительных библиотек в бандле.
 */

import { formatMoney, formatPercent, formatLeverValue } from '../format';
import { sanitizeAiReport, sanitizeRegistrationPayload } from '../../../utils/inputSecurity';

const escapeHtml = (value) =>
  String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const STATUS_LABEL = {
  green: 'Цель достижима',
  yellow: 'Цель на пределе',
  red: 'Цель выше потолка',
};

const STATUS_COLOR = {
  green: '#2a7a52',
  yellow: '#b7791f',
  red: '#c84b2f',
};

const buildStyles = () => `
  @page { size: A4; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    background: #ffffff;
    color: #1a1814;
    font-family: 'Fontin Sans', Georgia, system-ui, sans-serif;
    font-size: 11pt;
    line-height: 1.6;
  }
  .doc { max-width: 180mm; margin: 0 auto; }
  .brand { display: flex; justify-content: space-between; align-items: baseline;
    padding-bottom: 10px; border-bottom: 2px solid #1a1814; margin-bottom: 24px; }
  .brand-name { font-size: 18pt; font-weight: 700; letter-spacing: -0.02em; }
  .brand-name em { font-style: normal; color: #c84b2f; }
  .brand-meta { font-size: 9pt; color: rgba(26,24,20,0.5); }
  h1 { font-size: 21pt; line-height: 1.2; margin: 0 0 6px; letter-spacing: -0.02em; }
  h2 { font-size: 13pt; margin: 26px 0 10px; padding-bottom: 6px;
    border-bottom: 1px solid rgba(0,0,0,0.12); page-break-after: avoid; }
  h3 { font-size: 11.5pt; margin: 16px 0 6px; page-break-after: avoid; }
  p { margin: 0 0 10px; }
  .lead { color: rgba(26,24,20,0.68); margin-bottom: 20px; }
  .status { display: inline-block; padding: 8px 14px; border-radius: 4px;
    font-weight: 700; font-size: 11pt; margin-bottom: 8px; }
  .grid { display: flex; flex-wrap: wrap; gap: 10px; margin: 12px 0 4px; }
  .cell { flex: 1 1 30%; min-width: 46mm; padding: 10px 12px;
    background: #f5f3ee; border-left: 3px solid #c84b2f; }
  .cell-label { display: block; font-size: 8.5pt; text-transform: uppercase;
    letter-spacing: 0.06em; color: rgba(26,24,20,0.5); margin-bottom: 3px; }
  .cell-value { display: block; font-size: 13pt; font-weight: 700; }
  .scenario { padding: 12px 14px; background: #f5f3ee; margin-bottom: 12px;
    page-break-inside: avoid; }
  .scenario-head { display: flex; justify-content: space-between;
    align-items: baseline; margin-bottom: 6px; }
  .scenario-name { font-size: 12pt; font-weight: 700; }
  .scenario-tag { font-size: 9pt; color: #c84b2f; font-weight: 700; }
  .lever { padding: 6px 0; border-top: 1px solid rgba(0,0,0,0.08); font-size: 10pt; }
  .lever-name { font-weight: 700; }
  .muted { color: rgba(26,24,20,0.5); font-size: 9pt; }
  ol, ul { margin: 0 0 12px; padding-left: 18px; }
  li { margin-bottom: 7px; }
  .note { padding: 10px 12px; background: #fdf0ed; border-left: 3px solid #c84b2f;
    font-size: 9.5pt; margin: 14px 0; page-break-inside: avoid; }
  .footer { margin-top: 28px; padding-top: 10px;
    border-top: 1px solid rgba(0,0,0,0.12); font-size: 8.5pt;
    color: rgba(26,24,20,0.5); }
  @media print { .no-print { display: none; } }
`;

const renderCurrentState = (result) => `
  <h2>Где вы сейчас</h2>
  <div class="grid">
    <div class="cell">
      <span class="cell-label">Выручка в месяц</span>
      <span class="cell-value">${escapeHtml(formatMoney(result.state.revenue))}</span>
    </div>
    <div class="cell">
      <span class="cell-label">Средний чек</span>
      <span class="cell-value">${escapeHtml(formatMoney(result.state.averageCheck))}</span>
    </div>
    <div class="cell">
      <span class="cell-label">Клиентов в месяц</span>
      <span class="cell-value">${escapeHtml(Math.round(result.state.clients).toLocaleString('ru-RU'))}</span>
    </div>
    ${
      result.state.hasConversion
        ? `<div class="cell">
             <span class="cell-label">Доходит до покупки</span>
             <span class="cell-value">${escapeHtml(formatPercent(result.state.conversionPercent, 1))}</span>
           </div>`
        : ''
    }
    <div class="cell">
      <span class="cell-label">Потолок при этой модели</span>
      <span class="cell-value">${escapeHtml(formatMoney(result.ceiling.revenue))}</span>
    </div>
  </div>
`;

const renderPrimaryLever = (result) => {
  if (!result.primaryLever) return '';

  const { lever, capPercent, gain } = result.primaryLever;

  return `
    <h2>Ваш главный резерв</h2>
    <h3>${escapeHtml(lever.plainLabel)}</h3>
    <p>${escapeHtml(lever.action)}. В вашей нише это реально поднять примерно на
      ${escapeHtml(formatPercent(capPercent))} — это ${escapeHtml(formatMoney(gain))}
      дополнительной выручки в месяц.</p>
    <p class="muted">Срок: ${escapeHtml(lever.timing.label)}</p>
  `;
};

const renderScenarios = (result) => {
  if (!result.scenarios.length) return '';

  const recommendedId = result.recommended ? result.recommended.id : null;

  const cards = result.scenarios
    .map((scenario) => {
      const levers = scenario.levers
        .filter((lever) => lever.growth > 0)
        .map(
          (lever) => `
            <div class="lever">
              <span class="lever-name">${escapeHtml(lever.plainLabel)}</span> —
              ${escapeHtml(formatPercent(lever.growthPercent, 1))}
              (${escapeHtml(formatLeverValue(lever.id, lever.currentValue))} →
              ${escapeHtml(formatLeverValue(lever.id, lever.targetValue))})${
                lever.atCap ? ' <span class="muted">предел ниши</span>' : ''
              }
              <div class="muted">${escapeHtml(lever.how)}</div>
            </div>`,
        )
        .join('');

      return `
        <div class="scenario">
          <div class="scenario-head">
            <span class="scenario-name">${escapeHtml(scenario.name)}</span>
            ${scenario.id === recommendedId ? '<span class="scenario-tag">рекомендуем</span>' : ''}
          </div>
          <p class="muted">${escapeHtml(scenario.description)}</p>
          <p>Выручка станет <strong>${escapeHtml(formatMoney(scenario.achievedRevenue))}</strong>
            за ${escapeHtml(scenario.timing.label)}.
            ${
              scenario.closesTarget
                ? 'Цель закрывается полностью.'
                : `Закрывает ${escapeHtml(formatPercent(scenario.targetCoverage))} нужного прироста.`
            }</p>
          ${levers}
        </div>`;
    })
    .join('');

  return `<h2>Три пути к цели</h2>${cards}`;
};

const renderAiReport = (aiReport) => {
  if (!aiReport) return '';

  const steps = (aiReport.steps || [])
    .map(
      (step) =>
        `<li><strong>${escapeHtml(step.title)}</strong><br>${escapeHtml(step.text)}</li>`,
    )
    .join('');

  const risks = (aiReport.risks || []).map((risk) => `<li>${escapeHtml(risk)}</li>`).join('');

  return `
    <h2>Что это значит для вас</h2>
    ${aiReport.summary ? `<p>${escapeHtml(aiReport.summary)}</p>` : ''}
    ${steps ? `<h3>Что делать</h3><ol>${steps}</ol>` : ''}
    ${risks ? `<h3>На что смотреть внимательно</h3><ul>${risks}</ul>` : ''}
  `;
};

const renderChannels = (result) => {
  if (!result.channels) return '';

  const items = result.channels.channels
    .map(
      (channel) =>
        `<li><strong>${escapeHtml(channel.name)}</strong> — ${escapeHtml(channel.purpose)}</li>`,
    )
    .join('');

  return `
    <h2>Где искать клиентов</h2>
    <ul>${items}</ul>
    ${result.channels.budgetNote ? `<p class="muted">${escapeHtml(result.channels.budgetNote)}</p>` : ''}
    <p class="muted">Источник: ${escapeHtml(result.channels.source)}</p>
  `;
};

const buildDocumentHtml = ({ result, aiReport, full, person }) => {
  const date = new Date().toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const evaluation = result.targetEvaluation;
  const statusBlock =
    evaluation && evaluation.valid
      ? `<div class="status" style="background:${STATUS_COLOR[evaluation.status]}1a;color:${
          STATUS_COLOR[evaluation.status]
        }">${escapeHtml(STATUS_LABEL[evaluation.status])}</div>
         <p>${escapeHtml(evaluation.text)}</p>`
      : '';

  return `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="utf-8">
  <title>marketDir — разбор бизнеса</title>
  <style>${buildStyles()}</style>
</head>
<body>
  <div class="doc">
    <div class="brand">
      <span class="brand-name">market<em>Dir</em></span>
      <span class="brand-meta">${escapeHtml(date)}</span>
    </div>

    <h1>${full ? 'Полный разбор' : 'Краткий результат диагностики'}</h1>
    <p class="lead">
      ${escapeHtml(result.niche.label)}${person && person.name ? ` · ${escapeHtml(person.name)}` : ''}
      · цель ${escapeHtml(formatMoney(result.targetRevenue))} в месяц
    </p>

    ${statusBlock}
    ${renderCurrentState(result)}
    ${renderPrimaryLever(result)}
    ${full ? renderScenarios(result) : ''}
    ${full ? renderAiReport(aiReport) : ''}
    ${full ? renderChannels(result) : ''}

    <div class="note">${escapeHtml(result.disclaimers.ceiling)}</div>
    ${full ? `<div class="note">${escapeHtml(result.disclaimers.timing)}</div>` : ''}

    <div class="footer">
      marketDir — считайте маркетинг, а не угадывайте.
      Документ построен на цифрах, которые вы ввели ${escapeHtml(date)}.
      ${full ? '' : 'Полный разбор с тремя сценариями и планом доступен на сайте.'}
    </div>
  </div>
</body>
</html>`;
};

export const exportDiagnosticReport = ({ result, aiReport, full, person }) => {
  if (!result || !result.valid) return false;

  const safePerson = person ? sanitizeRegistrationPayload(person) : null;
  const safeAiReport = aiReport ? sanitizeAiReport(aiReport) || aiReport : null;

  const html = buildDocumentHtml({
    result,
    aiReport: safeAiReport,
    full,
    person: safePerson,
  });
  const printWindow = window.open('', '_blank', 'noopener,noreferrer');

  if (!printWindow) {
    window.alert(
      'Браузер заблокировал новое окно. Разрешите всплывающие окна для этого сайта и попробуйте снова.',
    );
    return false;
  }

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  printWindow.addEventListener('load', () => {
    printWindow.focus();
    printWindow.print();
  });

  return true;
};

export { buildDocumentHtml };
